import { useEffect, useRef, useState } from 'react'
import { MenuSubSubItem, SubEnterParams } from '../menuTypes'
import { useIsTouchDevice } from './useIsTouchDevice'

export function useHoverMenu() {
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [subOpen, setSubOpen] = useState<{
    name: string
    rowIndex: number
  } | null>(null)
  const [subMenuData, setSubMenuData] = useState<MenuSubSubItem[][] | null>(
    null,
  )

  const [subTriggerRect, setSubTriggerRect] = useState<DOMRect | null>(null)
  const closeTimeout = useRef<NodeJS.Timeout | null>(null)
  const pointerInMenu = useRef(false)
  const pointerInSubmenu = useRef(false)

  const isTouchDevice = useIsTouchDevice()

  const cancelClose = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current)
  }

  const handleEnter = (name: string, hasSubmenu: boolean) => {
    if (!hasSubmenu) return
    cancelClose()
    setOpen(name)
  }

  const closeMenus = () => {
    setOpen(null)
    setSubOpen(null)
    setSubMenuData(null)
  }

  const handleLeave = () => {
    if (isTouchDevice) return
    closeTimeout.current = setTimeout(() => {
      setOpen(null)
      setSubOpen(null)
      setSubMenuData(null)
    }, 200)
  }

  const handleSubEnter = ({
    name,
    rowIndex,
    hasSubmenu,
    subs,
    trigger,
  }: SubEnterParams) => {
    if (!hasSubmenu || !subs?.some((col) => col.length > 0)) return
    cancelClose()
    setSubOpen({ name, rowIndex })
    setSubMenuData(subs)
    if (trigger) setSubTriggerRect(trigger.getBoundingClientRect())
  }

  const handleSubLeave = () => {
    if (isTouchDevice) return
    closeTimeout.current = setTimeout(() => {
      if (!pointerInSubmenu.current) {
        setSubOpen(null)
        setSubMenuData(null)
        if (!pointerInMenu.current) {
          setOpen(null)
        }
      }
    }, 200)
  }

  const hoverMenuProps = {
    onMouseEnter: () => {
      pointerInMenu.current = true
    },
    onMouseLeave: () => {
      pointerInMenu.current = false
    },
  }

  const floatingSubmenuProps = {
    data: subMenuData,
    top: subTriggerRect?.bottom + 12,
    onEnter: () => {
      pointerInSubmenu.current = true
      cancelClose()
    },
    onLeave: () => {
      pointerInSubmenu.current = false
      handleSubLeave()
    },
  }

  const showFloatingSubmenu = subOpen && subMenuData && subTriggerRect

  return {
    menuRef,
    open,
    subOpen,
    subMenuData,
    subTriggerRect,
    handleEnter,
    handleLeave,
    handleSubEnter,
    handleSubLeave,
    cancelClose,
    pointerInSubmenu,
    pointerInMenu,
    isTouchDevice,
    closeMenus,
    hoverMenuProps,
    floatingSubmenuProps,
    showFloatingSubmenu,
  }
}
